import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  TextField,
  makeStyles,
  MenuItem,
  Grid,
  Backdrop,
  CircularProgress,
  Typography,
} from "@material-ui/core";

import { Autocomplete } from "@material-ui/lab";
import { useAlert } from "react-alert";

import {
  createPrivateSale,
} from "../../../adminModel/privateSale";

import {
  getGroup,
  getCategory,
  findArtist,
} from "../../../adminModel/group";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },

  card: {
    border: "1px solid #e5e5e5",
    borderRadius: 8,
    boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
    overflow: "hidden",
  },

  cardHeader: {
    backgroundColor: "#fafafa",
    padding: theme.spacing(2.5, 3),
  },

  cardTitle: {
    fontSize: 17,
    fontWeight: 600,
    color: "#222",
  },

  cardSubtitle: {
    fontSize: 12,
    color: "#888",
    marginTop: 4,
  },

  cardContent: {
    padding: theme.spacing(3),
  },

  column: {
    width: "100%",
  },

  section: {
    marginBottom: theme.spacing(3),
  },

  sectionHeader: {
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: "1px solid #eeeeee",
  },

  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#333",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },

  sectionDescription: {
    fontSize: 11,
    color: "#999",
    marginTop: 3,
  },

  field: {
    marginTop: theme.spacing(1.5),

    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      borderRadius: 6,
    },

    "& .MuiInputBase-input": {
      fontSize: 13,
    },

    "& .MuiInputLabel-root": {
      fontSize: 13,
    },
  },

  multilineField: {
    marginTop: theme.spacing(1.5),

    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      borderRadius: 6,
      alignItems: "flex-start",
    },

    "& .MuiInputBase-input": {
      fontSize: 13,
      lineHeight: 1.6,
    },

    "& .MuiInputLabel-root": {
      fontSize: 13,
    },
  },

  artistField: {
    marginTop: theme.spacing(1.5),

    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      borderRadius: 6,
    },

    "& .MuiInputBase-input": {
      fontSize: 13,
    },

    "& .MuiInputLabel-root": {
      fontSize: 13,
    },
  },

  footer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: theme.spacing(1.5, 3),
    backgroundColor: "#fafafa",
    gap: theme.spacing(1),
  },

  saveButton: {
    minWidth: 130,
    height: 38,
    borderRadius: 6,
    textTransform: "none",
    fontSize: 13,
    fontWeight: 600,
    boxShadow: "none",

    "&:hover": {
      boxShadow: "none",
    },
  },

  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },

  helperText: {
    fontSize: 11,
    color: "#999",
    marginTop: 5,
  },

  "@media (max-width: 960px)": {
    cardContent: {
      padding: theme.spacing(2),
    },
  },
}));

const Create = ({ className, ...rest }) => {
  const classes = useStyles();
  const alert = useAlert();

  const [loading, setLoading] = useState(false);

  const [group, setGroup] = useState([]);
  const [category, setCategory] = useState([]);

  // =========================
  // ARTIST AUTOCOMPLETE
  // =========================

  const [artistOptions, setArtistOptions] = useState([]);
  const [artistLoading, setArtistLoading] = useState(false);

  // =========================
  // FORM VALUES
  // =========================

  const [values, setValues] = useState({
    privateSalesGroup: "",
    artist: "",
    signed_dated: "",
    medium: "",
    size: "",
    title: "",
    property: "",
    coa: "",
    provenance: "",
    exhibited: "",
    literature: "",
    write_up: "",
    lot_group: "",
    lot_details_html: "",
    alias: "",
  });

  // =========================
  // LOAD GROUP / CATEGORY
  // =========================

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const [groups, categories] = await Promise.all([
        getGroup(),
        getCategory(),
      ]);

      setGroup(
        groups.filter(
          (x) => x.groupname !== "EXHIBITION"
        )
      );

      setCategory(categories);
    } catch (err) {
      console.error(err);
      alert.error(
        "Failed to load initial data"
      );
    } finally {
      setLoading(false);
    }
  }, [alert]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // =========================
  // NORMAL INPUT
  // =========================

  const handleChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =========================
  // ARTIST SEARCH
  // =========================

  const handleArtistSearch = async (
    event,
    newInputValue
  ) => {
    setValues((prev) => ({
      ...prev,
      artist: newInputValue,
    }));

    if (
      !newInputValue ||
      newInputValue.length < 2
    ) {
      setArtistOptions([]);
      return;
    }

    setArtistLoading(true);

    try {
      const res =
        await findArtist(newInputValue);

      const actualData =
        res.data || res;

      if (
        actualData &&
        Array.isArray(actualData)
      ) {
        const formattedArtists =
          actualData
            .map((a) =>
              `${a.firstname || ""} ${
                a.lastname || ""
              }`.trim()
            )
            .filter(Boolean);

        setArtistOptions(
          formattedArtists
        );
      } else {
        setArtistOptions([]);
      }
    } catch (err) {
      console.error(
        "Artist search error:",
        err
      );

      setArtistOptions([]);
    } finally {
      setArtistLoading(false);
    }
  };

  // =========================
  // SUBMIT
  // =========================

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const result =
        await createPrivateSale(values);

      if (
        result.sucess === true ||
        result.success === true
      ) {
        alert.success(
          result.msg ||
            "Successfully created!"
        );
      } else {
        alert.error(
          result.msg ||
            "Error creating entry"
        );
      }
    } catch (err) {
      console.error(err);

      alert.error(
        "An unexpected error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={clsx(
        classes.root,
        className
      )}
      {...rest}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <form
            onSubmit={handleFormSubmit}
          >
            <Card
              className={classes.card}
            >
              {/* =========================
                  HEADER
              ========================= */}

              <CardHeader
                className={
                  classes.cardHeader
                }
                title={
                  <Typography
                    className={
                      classes.cardTitle
                    }
                  >
                    Data Entry
                  </Typography>
                }
                subheader={
                  <Typography
                    className={
                      classes.cardSubtitle
                    }
                  >
                    Create a new private sale
                    entry
                  </Typography>
                }
              />

              <Divider />

              <CardContent
                className={
                  classes.cardContent
                }
              >
                <Grid
                  container
                  spacing={4}
                >
                  {/* =========================
                      LEFT COLUMN
                  ========================= */}

                  <Grid
                    item
                    md={6}
                    xs={12}
                  >
                    <div
                      className={
                        classes.section
                      }
                    >
                      <div
                        className={
                          classes.sectionHeader
                        }
                      >
                        <Typography
                          className={
                            classes.sectionTitle
                          }
                        >
                          Artwork Information
                        </Typography>

                        <Typography
                          className={
                            classes.sectionDescription
                          }
                        >
                          Basic artwork and
                          classification details
                        </Typography>
                      </div>

                      {/* GROUP */}

                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        select
                        required
                        label="Group"
                        name="privateSalesGroup"
                        variant="outlined"
                        value={
                          values.privateSalesGroup
                        }
                        onChange={
                          handleChange
                        }
                      >
                        {group.map(
                          (val) => (
                            <MenuItem
                              key={val.id}
                              value={
                                val.groupname
                              }
                            >
                              {
                                val.groupname
                              }
                            </MenuItem>
                          )
                        )}
                      </TextField>

                      {/* ARTIST */}

                      <Autocomplete
                        className={
                          classes.artistField
                        }
                        freeSolo
                        options={
                          artistOptions
                        }
                        loading={
                          artistLoading
                        }
                        inputValue={
                          values.artist
                        }
                        onInputChange={
                          handleArtistSearch
                        }
                        onChange={(
                          event,
                          newValue
                        ) => {
                          setValues(
                            (prev) => ({
                              ...prev,
                              artist:
                                newValue ||
                                "",
                            })
                          );
                        }}
                        renderInput={(
                          params
                        ) => (
                          <TextField
                            {...params}
                            label="Artist"
                            variant="outlined"
                            fullWidth
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <React.Fragment>
                                  {artistLoading ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={18}
                                    />
                                  ) : null}

                                  {
                                    params
                                      .InputProps
                                      .endAdornment
                                  }
                                </React.Fragment>
                              ),
                            }}
                          />
                        )}
                      />

                      {/* TITLE */}

                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        label="Title"
                        name="title"
                        variant="outlined"
                        value={
                          values.title
                        }
                        onChange={
                          handleChange
                        }
                      />

                      {/* MEDIUM */}

                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        label="Medium"
                        name="medium"
                        variant="outlined"
                        value={
                          values.medium
                        }
                        onChange={
                          handleChange
                        }
                      />

                      {/* SIGNED DATE */}

                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        label="Signed / Dated"
                        name="signed_dated"
                        variant="outlined"
                        value={
                          values.signed_dated
                        }
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />

                      {/* CATEGORY */}

                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        select
                        label="Category"
                        name="lot_group"
                        variant="outlined"
                        value={
                          values.lot_group
                        }
                        onChange={
                          handleChange
                        }
                      >
                        {category.map(
                          (
                            val,
                            i
                          ) => (
                            <MenuItem
                              key={i}
                              value={
                                val.categories
                              }
                            >
                              {
                                val.categories
                              }
                            </MenuItem>
                          )
                        )}
                      </TextField>

                      {/* SIZE */}

                      <TextField
                        className={
                          classes.multilineField
                        }
                        fullWidth
                        multiline
                        rows={3}
                        label="Size"
                        name="size"
                        variant="outlined"
                        value={
                          values.size
                        }
                        onChange={
                          handleChange
                        }
                      />
                    </div>
                  </Grid>

                  {/* =========================
                      RIGHT COLUMN
                  ========================= */}

                  <Grid
                    item
                    md={6}
                    xs={12}
                  >
                    <div
                      className={
                        classes.section
                      }
                    >
                      <div
                        className={
                          classes.sectionHeader
                        }
                      >
                        <Typography
                          className={
                            classes.sectionTitle
                          }
                        >
                          Additional Details
                        </Typography>

                        <Typography
                          className={
                            classes.sectionDescription
                          }
                        >
                          Supporting information
                          and documentation
                        </Typography>
                      </div>

                      {/* PROPERTY */}

                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        label="Property"
                        name="property"
                        variant="outlined"
                        value={
                          values.property
                        }
                        onChange={
                          handleChange
                        }
                      />

                      {/* PROVENANCE */}

                      <TextField
                        className={
                          classes.multilineField
                        }
                        fullWidth
                        multiline
                        minRows={3}
                        label="Provenance"
                        name="provenance"
                        variant="outlined"
                        value={
                          values.provenance
                        }
                        onChange={
                          handleChange
                        }
                      />

                      {/* COA */}

                      <TextField
                        className={
                          classes.multilineField
                        }
                        fullWidth
                        multiline
                        minRows={3}
                        label="COA"
                        name="coa"
                        variant="outlined"
                        value={
                          values.coa
                        }
                        onChange={
                          handleChange
                        }
                      />

                      {/* EXHIBITED */}

                      <TextField
                        className={
                          classes.multilineField
                        }
                        fullWidth
                        multiline
                        minRows={3}
                        label="Exhibited"
                        name="exhibited"
                        variant="outlined"
                        value={
                          values.exhibited
                        }
                        onChange={
                          handleChange
                        }
                      />

                      {/* LITERATURE */}

                      <TextField
                        className={
                          classes.multilineField
                        }
                        fullWidth
                        multiline
                        minRows={3}
                        label="Literature"
                        name="literature"
                        variant="outlined"
                        value={
                          values.literature
                        }
                        onChange={
                          handleChange
                        }
                      />

                      {/* ALIAS */}

                      <TextField
                        className={
                          classes.multilineField
                        }
                        fullWidth
                        multiline
                        minRows={3}
                        label="Alias"
                        name="alias"
                        variant="outlined"
                        value={
                          values.alias
                        }
                        onChange={
                          handleChange
                        }
                      />

                      {/* WRITE UP */}

                      <TextField
                        className={
                          classes.multilineField
                        }
                        fullWidth
                        multiline
                        minRows={3}
                        label="Write Up"
                        name="write_up"
                        variant="outlined"
                        value={
                          values.write_up
                        }
                        onChange={
                          handleChange
                        }
                      />
                    </div>
                  </Grid>
                </Grid>
              </CardContent>

              <Divider />

              {/* =========================
                  FOOTER
              ========================= */}

              <Box
                className={
                  classes.footer
                }
              >
                <Button
                  className={
                    classes.saveButton
                  }
                  color="primary"
                  variant="contained"
                  type="submit"
                >
                  Save Details
                </Button>
              </Box>
            </Card>
          </form>
        </Grid>
      </Grid>

      {/* =========================
          LOADING
      ========================= */}

      <Backdrop
        className={
          classes.backdrop
        }
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

Create.propTypes = {
  className: PropTypes.string,
};

export default Create;